import React, { useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import { parsePayload } from '../../utils/decoding';
import { Scan } from 'lucide-react';
import './QRUpload.css';

export const QRUpload: React.FC = () => {
  const { t } = useTranslation('create');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setPayloadType = useAppStore(state => state.setPayloadType);
  const setScannedData = useAppStore(state => state.setScannedData);
  const setToast = useAppStore(state => state.setToast);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setToast({ message: t('qrupload.loadError', 'Error loading the image.'), type: 'error' });
      e.target.value = '';
      return;
    }

    const processFile = async () => {
      let html5QrCode: Html5Qrcode | null = null;
      const originalConsoleError = console.error;
      try {
        console.error = (...args: unknown[]) => {
          const msg = args.map(a => (a instanceof Error ? a.message : String(a))).join(' ');
          if (
            msg.includes('NotFoundException') ||
            msg.includes('No QR code found') ||
            msg.includes('No MultiFormat Readers')
          ) {
            return;
          }
          originalConsoleError.apply(console, args);
        };

        html5QrCode = new Html5Qrcode("qr-dummy-reader");
        const resultText = await html5QrCode.scanFileV2(file, true);

        const payload = parsePayload(resultText.decodedText || (resultText as unknown as string));
        setPayloadType(payload.type);
        setScannedData(payload.data);
      } catch (err) {
        const errStr = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
        if (
          !errStr.includes('NotFoundException') &&
          !errStr.includes('No QR code found') &&
          !errStr.includes('No MultiFormat Readers')
        ) {
          originalConsoleError(err);
        }
        setToast({ message: t('qrupload.notFound', 'No valid QR code found in the image.'), type: 'error' });
      } finally {
        console.error = originalConsoleError;
        if (html5QrCode && typeof html5QrCode.clear === 'function') {
          try {
            html5QrCode.clear();
          } catch {
            // Ignore clear errors if dummy reader DOM node was modified
          }
        }
      }
    };

    void processFile();
    e.target.value = '';
  };

  return (
    <div className="qr-upload-wrapper">
      <div id="qr-dummy-reader" style={{ display: 'none' }}></div>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id="qr-upload-input"
      />
      <button 
        className="type-tab scan-tab" 
        onClick={() => fileInputRef.current?.click()}
        aria-label={t('qrupload.scan', 'Scan')}
      >
        <Scan size={18} />
        <span>{t('qrupload.scan', 'Scan')}</span>
      </button>
    </div>
  );
};
